import { useState, useEffect } from 'react'
import { Tag } from '../components/Tag'
import { api, type ApiArtist, type ApiArtistAvailability } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { isSafeUrl } from '../utils/url'
import styles from './ArtistsScreen.module.css'

export function ArtistsScreen() {
  const [artists, setArtists] = useState<ApiArtist[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [selected, setSelected] = useState<ApiArtist | null>(null)
  const [availability, setAvailability] = useState<ApiArtistAvailability[]>([])
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(false)
  const [formStart, setFormStart] = useState('')
  const [formEnd, setFormEnd] = useState('')
  const [formStatus, setFormStatus] = useState('available')
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [createName, setCreateName] = useState('')
  const [createBio, setCreateBio] = useState('')
  const [createMusicGenre, setCreateMusicGenre] = useState('')
  const [createWebsite, setCreateWebsite] = useState('')
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const { user } = useAuth()

  useEffect(() => {
    api.artists.list()
      .then(setArtists)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    const website = createWebsite.trim()
    if (website && !isSafeUrl(website)) {
      setCreateError('Please enter a valid http(s) URL.')
      return
    }
    setCreateSubmitting(true)
    setCreateError(null)
    try {
      // Self-serve claim: link the new profile to the logged-in member.
      // The backend 403s any memberId that isn't the caller (unless admin).
      const created = await api.artists.create({
        name: createName,
        bio: createBio || null,
        musicGenre: createMusicGenre || null,
        website: website || null,
        memberId: user.id,
      })
      setCreateName('')
      setCreateBio('')
      setCreateMusicGenre('')
      setCreateWebsite('')
      // Refresh the directory list, then open the new artist's detail view.
      api.artists.list()
        .then(setArtists)
        .catch(() => setLoadError(true))
      setSelected(created)
      loadAvailability(created.id)
    } catch (err) {
      // Backend 400s (e.g. "name is required") surface via request<T>'s
      // thrown Error — show the real message inline.
      setCreateError(err instanceof Error ? err.message : 'Failed to create artist.')
    } finally {
      setCreateSubmitting(false)
    }
  }

  const loadAvailability = (artistId: number) => {
    setAvailability([])
    setAvailabilityLoading(true)
    setAvailabilityError(false)
    api.artists.listAvailability(artistId)
      .then(setAvailability)
      .catch(() => setAvailabilityError(true))
      .finally(() => setAvailabilityLoading(false))
  }

  const handleSelect = (artist: ApiArtist) => {
    setSelected(artist)
    setFormStart('')
    setFormEnd('')
    setFormStatus('available')
    setFormError(null)
    loadAvailability(artist.id)
  }

  const handleAddAvailability = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected) return
    setFormSubmitting(true)
    setFormError(null)
    try {
      await api.artists.addAvailability(selected.id, {
        startAt: new Date(formStart).toISOString(),
        endAt: new Date(formEnd).toISOString(),
        status: formStatus,
      })
      setFormStart('')
      setFormEnd('')
      setFormStatus('available')
      loadAvailability(selected.id)
    } catch (err) {
      // Backend 400s (e.g. "endAt must be after startAt") surface via
      // request<T>'s thrown Error — show the real message inline.
      setFormError(err instanceof Error ? err.message : 'Failed to add availability.')
    } finally {
      setFormSubmitting(false)
    }
  }

  const formatWindow = (a: ApiArtistAvailability) => {
    const start = new Date(a.start_at)
    const end = new Date(a.end_at)
    const sameDay = start.toDateString() === end.toDateString()
    if (sameDay) {
      return `${start.toLocaleDateString()} · ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}–${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
    return `${start.toLocaleDateString()} → ${end.toLocaleDateString()}`
  }

  if (selected) {
    // Same rule the backend enforces on POST /:id/availability:
    // the artist themself (member_id matches) or an admin may add windows.
    const canEdit = user && (user.id === selected.member_id || user.role === 'admin')

    return (
      <div className={styles.container}>
        <button className={styles.backButton} onClick={() => setSelected(null)}>
          ← Back to artists
        </button>

        <h2 className={styles.headerTitle}>{selected.name}</h2>
        {selected.music_genre && (
          <Tag variant="neutral">{selected.music_genre}</Tag>
        )}
        {selected.member_name && (
          <p className={styles.memberLink}>Member: {selected.member_name}</p>
        )}
        {selected.bio && <p className={styles.bio}>{selected.bio}</p>}
        {selected.website && isSafeUrl(selected.website) && (
          <p>
            <a href={selected.website} target="_blank" rel="noopener noreferrer" className={styles.websiteLink}>
              {selected.website}
            </a>
          </p>
        )}

        <h3 className={styles.sectionTitle}>Availability</h3>
        {availabilityLoading ? (
          <div className={styles.loading}>Loading availability...</div>
        ) : availabilityError ? (
          <div className={styles.errorText}>Failed to load availability.</div>
        ) : availability.length === 0 ? (
          <div className={styles.emptyText}>No availability windows yet.</div>
        ) : (
          <div className={styles.availabilityList}>
            {availability.map(a => (
              <div key={a.id} className={styles.availabilityItem}>
                <span className={styles.availabilityWindow}>{formatWindow(a)}</span>
                <Tag variant={a.status === 'available' ? 'accent' : 'neutral'}>
                  {a.status === 'available' ? 'Available' : 'Unavailable'}
                </Tag>
              </div>
            ))}
          </div>
        )}

        {canEdit && (
          <form onSubmit={handleAddAvailability} className={styles.addForm}>
            <h3 className={styles.sectionTitle}>Add availability</h3>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>
                Start
                <input
                  type="datetime-local"
                  value={formStart}
                  onChange={e => setFormStart(e.target.value)}
                  className={styles.formInput}
                  required
                />
              </label>
              <label className={styles.formLabel}>
                End
                <input
                  type="datetime-local"
                  value={formEnd}
                  onChange={e => setFormEnd(e.target.value)}
                  className={styles.formInput}
                  required
                />
              </label>
              <label className={styles.formLabel}>
                Status
                <select
                  value={formStatus}
                  onChange={e => setFormStatus(e.target.value)}
                  className={styles.formInput}
                >
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </label>
            </div>
            {formError && (
              <div className={styles.formError}>{formError}</div>
            )}
            <button
              type="submit"
              className={styles.submitButton}
              disabled={formSubmitting || !formStart || !formEnd}
            >
              {formSubmitting ? 'Adding...' : 'Add availability'}
            </button>
          </form>
        )}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.headerTitle}>Artists</h2>
      <p className={styles.headerSubtitle}>
        The island's performers and their availability.
      </p>

      {loadError ? (
        <div className={styles.errorText}>Failed to load artists.</div>
      ) : loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : artists.length === 0 ? (
        <div className={styles.emptyText}>No artists yet.</div>
      ) : (
        <div className={styles.artistList}>
          {artists.map(artist => (
            <button
              key={artist.id}
              className={styles.artistCard}
              onClick={() => handleSelect(artist)}
            >
              <div className={styles.artistCardInfo}>
                <div className={styles.artistName}>{artist.name}</div>
                {artist.music_genre && (
                  <Tag variant="neutral">{artist.music_genre}</Tag>
                )}
              </div>
              {artist.member_name && (
                <span className={styles.memberLink}>{artist.member_name}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {user && (
        <form onSubmit={handleCreate} className={styles.addForm}>
          <h3 className={styles.sectionTitle}>Create/claim artist profile</h3>
          <p className={styles.formHint}>
            Claim this artist profile for yourself — it will be linked to your
            account so you can manage your own availability.
          </p>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              Name
              <input
                type="text"
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                className={styles.formInput}
              />
            </label>
            <label className={styles.formLabel}>
              Music genre
              <input
                type="text"
                value={createMusicGenre}
                onChange={e => setCreateMusicGenre(e.target.value)}
                className={styles.formInput}
              />
            </label>
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              Bio
              <textarea
                value={createBio}
                onChange={e => setCreateBio(e.target.value)}
                className={styles.formInput}
                rows={3}
              />
            </label>
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              Website
              <input
                type="text"
                value={createWebsite}
                onChange={e => setCreateWebsite(e.target.value)}
                className={styles.formInput}
                placeholder="https://"
              />
            </label>
          </div>
          {createError && (
            <div className={styles.formError}>{createError}</div>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={createSubmitting}
          >
            {createSubmitting ? 'Creating...' : 'Create/claim artist profile'}
          </button>
        </form>
      )}
    </div>
  )
}
